import React, { FC, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import { PurchaseStore } from "../store";
import {
  CheckOutRecommendedItemDetails,
  CustomerFacingCheckOutItemModel,
  CustomOptionModel,
} from "../models";
import { ICheckOutCombinedColors } from "../interfaces";
import { CheckOutButtonStyle, CheckOutRedirectTypes } from "../enums";
import NavPills from "./NavPills";
import { MainOuterCard, MultiplePriceWrapper } from "../styles";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Regex,
  TopBanner,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import PurchasePriceDescriptionRow, {
  AmountRow,
} from "./PurchasePriceDescriptionRow";
import CarouselComponent from "./CarouselComponent";
import CustomPricingInput from "./CustomPricingInput";
import ItemNotAvailableWarning from "./ItemNotAvailableWarning";
import CarouselWithThumbnail from "./CarousalWithThumbnail";
import FreeIncludedItems from "./FreeIncludedItems";
import styled from "styled-components";

type IProps = {
  purchaseStore: PurchaseStore;
  items: CustomerFacingCheckOutItemModel[];
  navigate: NavigateFunction;
  onPurchaseClick: (item: CustomerFacingCheckOutItemModel) => void;
  groupByBillingFrequency: boolean;
  recommendedItem?: CheckOutRecommendedItemDetails;
  colors: ICheckOutCombinedColors;
  buttonCaption: string;
  buttonStyle: keyof typeof CheckOutButtonStyle;
  updateItems: (items: CustomerFacingCheckOutItemModel[]) => void;
  customOption?: CustomOptionModel;
  allowIncludedItems?: boolean;
};

interface IStyledProps {
  active?: boolean;
  width?: string;
  bundleSpacing?: number;
}

const BundleFlexWrapper = styled.div<IStyledProps>`
  height: calc(100vh - 382px);
  width: 100%;
  overflow: auto;
  padding-top: ${(props) =>
    props.bundleSpacing ? `${props.bundleSpacing}px` : "0px"};
  padding-bottom: 55px;
  padding-right: 30px;
  box-sizing: border-box;
  /* new CSS */
  overflow: unset;
  /* overflow-x: auto; */
  width: 100%;

  &::-webkit-scrollbar {
    width: 0.6em;
    height: 0.9em;
    background-color: #15283717;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    outline: 1px solid #ddd;
    background-color: rgba(21, 40, 55, 0.3);
    border-radius: 10px;
  }
  .main-image-wrapper {
    height: 150px !important;
  }
  @media (max-width: 1400px) {
    /* overflow-x: unset !important; */
    padding-right: 0px;
  }
  @media (max-width: 768px) {
    height: calc(100vh - 230px);
    overflow-x: unset !important;
  }
  @media (max-width: 1024px) {
    .bundle-flex {
      flex-wrap: wrap !important;
    }
  }
`;
const MultipleItems: FC<IProps> = ({
  purchaseStore,
  onPurchaseClick,
  items,
  groupByBillingFrequency,
  recommendedItem,
  colors,
  buttonCaption,
  buttonStyle,
  updateItems,
  customOption,
  allowIncludedItems,
}) => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!items || items.length === 0) return null;

  const groupedItems = groupByBillingFrequency
    ? CustomerFacingCheckOutItemModel.groupByBillingFrequency(
        items,
        customOption
      )
    : [];

  function navPills() {
    if (groupedItems.length <= 1) return null;

    const pills = groupedItems.map((item) => {
      return {
        key: item.name,
        title: item.name,
      };
    });
    return (
      <NavPills
        defaultActiveKey={pills[0].key}
        pills={pills}
        bgColor={colors?.tabs?.rgba}
        onSelect={(key) => purchaseStore.updateActiveNavPill(key)}
      />
    );
  }

  function renderItemsByActivePill() {
    if (groupedItems.length === 0) return renderItems(items);

    if (purchaseStore.activeNavPill === "") {
      purchaseStore.updateActiveNavPill(groupedItems[0].name);
    }
    const itemsToShow = groupedItems.find(
      (item) => item.name === purchaseStore.activeNavPill
    );

    return renderItems(
      itemsToShow ? itemsToShow.items : [],
      purchaseStore.activeNavPill
    );
  }

  const redirect = (customOption: CustomOptionModel) => {
    let redirectUrl = customOption.redirectValue;
    switch (CheckOutRedirectTypes[customOption.redirectType]) {
      case CheckOutRedirectTypes.Website:
        redirectUrl = customOption.redirectValue;
        if (!redirectUrl) return null;
        if (!Regex.httpProtocolRegex.test(redirectUrl)) {
          redirectUrl = "http://" + redirectUrl;
        }
        window.location.href = redirectUrl;
        break;
      case CheckOutRedirectTypes.ClickToEmail:
        if (Regex.validateEmail.test(redirectUrl)) {
          redirectUrl = `mailto:${redirectUrl}`;
        }
        break;
      case CheckOutRedirectTypes.ClickToCall:
        redirectUrl = `tel:${redirectUrl}`;
        break;
      case CheckOutRedirectTypes.ClickToText:
        redirectUrl = `sms:${redirectUrl}`;
        break;
      default:
        return null;
    }
  };

  function renderItems(
    itemsToShow: CustomerFacingCheckOutItemModel[],
    activeNavPill?: string
  ) {
    const anyOneOfTheItemHasMedia = itemsToShow.some(
      (item) => item.getAllMedias(true).length > 0
    );
    const isCustomOptionHasMedia = customOption?.media ? true : false || false;
    const classNameForMedia =
      anyOneOfTheItemHasMedia || isCustomOptionHasMedia ? "" : "no-media-item";
    const anyOneOfTheItemIsUnavailable = itemsToShow.some(
      (item) => item.isItemUnavailable
    );
    const itemUnavailableClass = anyOneOfTheItemIsUnavailable
      ? "item-unavailable"
      : "";
    const isShowCarousel = customOption?.media ? "" : "display-none-ss";
    const isShowDescription = customOption?.descriptionEditorRawContent
      ? ""
      : "display-none-ss";
    return (
      <>
        {itemsToShow.map((item, index) => {
          const recommendedData =
            recommendedItem?.itemId === item.itemId &&
            recommendedItem.priceId === item.price.id
              ? recommendedItem
              : undefined;
          const featuredBanner = recommendedData ? "featured-banner" : "";
          const isShowCarousel =
            item.getAllMedias(true).length === 0 ? "display-none-ss" : "";
          const isShowDescription = item.descriptionEditorRawContent
            ? ""
            : "display-none-ss";
          return (
            <div>
              <MainOuterCard
                key={index}
                border={`1px solid ${colors?.primary?.rgba}`}
                className={`position-relative ${featuredBanner} ${classNameForMedia}`}
              >
                {recommendedData && (
                  <TopBanner bgColor={colors?.recommended.rgba}>
                    <label>{recommendedData.caption}</label>
                  </TopBanner>
                )}

                <CustomLabel className="item-header" textAlign="center">
                  {item.name}
                </CustomLabel>
                <div className={`carousel-default-space ${isShowCarousel}`}>
                  <CarouselWithThumbnail
                    media={item.getAllMedias(true) || []}
                    className="option-carousel-wrapper"
                  />
                </div>
                {/* {item.description && <CustomLabel>{item.description}</CustomLabel>} */}

                {item.descriptionEditorRawContent && (
                  <label
                    className={`multiple-items-description ${isShowDescription}`}
                    dangerouslySetInnerHTML={{
                      __html: UtilityMethods.getEditorHtmlContent(
                        item.descriptionEditorRawContent
                      ),
                    }}
                  ></label>
                )}
                {item.isCustomPricing() && (
                  <CustomPricingInput
                    suggestedPrice={item.getCustomPricing()?.preset}
                    hasError={item.hasError("customPricingInputValue")}
                    value={item.customPricingInputValue}
                    onChange={(value) =>
                      updateCustomPricingInputValue(value, index)
                    }
                    errorMessage={item.getError("customPricingInputValue")}
                    className="multiple-custom-pricing"
                  />
                )}
                {!item.isCustomPricing() && (
                  <PurchasePriceDescriptionRow
                    billingType={item.price.billingType}
                    terms={item.price.terms}
                    recurrence={item.price.recurrence}
                    priceId={item.price.id}
                    itemType={item.itemType}
                    hasVariants={item.hasVariants}
                    variants={item.variants}
                    quantity={item.quantity}
                    className="price-centered-row price-centered-row-multiple"
                  />
                )}

                <div>
                  <FlexContainer
                    fxLayout="column"
                    spacing={0}
                    alignItems="center"
                  >
                    <CustomButton
                      bgColor={colors?.primary?.rgba}
                      onClick={() =>
                        !item.isItemUnavailable && onPurchaseClick(item)
                      }
                      roundedRect={
                        buttonStyle === CheckOutButtonStyle.SquareRoundCorner
                          ? true
                          : false
                      }
                      pillShape={
                        buttonStyle === CheckOutButtonStyle.Round ? true : false
                      }
                      disabled={item.isItemUnavailable}
                    >
                      {buttonCaption}
                    </CustomButton>
                    {itemUnavailableClass && (
                      <div className="item-unavailable-warning">
                        {item.isItemUnavailable && <ItemNotAvailableWarning />}
                      </div>
                    )}
                  </FlexContainer>
                </div>
                {/* {allowIncludedItems &&
                                item.includedItems &&
                                item.relatedItems?.includedItems.showSelectItems && (
                                    <CustomLine width="100%" bgColor="#ccc" height="1px" />
                                )}
                            {item.includedItems &&
                                item.includedItems.length > 0 &&
                                item.includedItems.map((includedItem) => {
                                    const imageMedia = includedItem.medias.find((m) => m.isImage());
                                    return (
                                        <RecommendedFreeProduct key={includedItem.id}>
                                            {imageMedia && <img src={imageMedia.fullURL} />}
                                            <CustomLabel>
                                                Get a <span className="highlighted-black">Free </span>{' '}
                                                <span className="semi-bold">{includedItem.name}</span> with purchase.
                                            </CustomLabel>
                                        </RecommendedFreeProduct>
                                    );
                                })} */}
                {allowIncludedItems &&
                item?.includedItems &&
                item?.includedItems?.length > 0 ? (
                  <div>
                    <FlexContainer
                      alignItems="center"
                      fxLayout="column"
                      spacing={5}
                      className="multiple-free-items"
                    >
                      <FreeIncludedItems includedItems={item.includedItems} />
                    </FlexContainer>
                  </div>
                ) : (
                  ""
                )}
              </MainOuterCard>
            </div>
          );
        })}
        {((customOption && activeNavPill === "CUSTOM") ||
          (!groupByBillingFrequency && customOption)) && (
          <div>
            <MainOuterCard
              border={`1px solid ${colors?.primary?.rgba}`}
              className={`position-relative ${classNameForMedia} custom-option-item-card`}
            >
              <CustomLabel className="item-header" textAlign="center">
                {customOption.name}
              </CustomLabel>
              {customOption.media && (
                <div className={`carousel-default-space ${isShowCarousel} `}>
                  <CarouselComponent
                    media={[customOption.media]}
                    className="option-carousel-wrapper"
                  />
                </div>
              )}
              {customOption.descriptionEditorRawContent && (
                <label
                  className={`multiple-items-description ${isShowDescription}`}
                  dangerouslySetInnerHTML={{
                    __html: UtilityMethods.getEditorHtmlContent(
                      customOption.descriptionEditorRawContent
                    ),
                  }}
                ></label>
              )}

              {customOption.showPrice && (
                <AmountRow className="price-centered-row price-centered-row-multiple">
                  <CustomLabel
                    fontWeight={"600"}
                    color="black"
                    fontSize={"25px"}
                  >
                    {customOption.priceText}
                  </CustomLabel>
                </AmountRow>
              )}
              {customOption.showButton && (
                <CustomButton
                  bgColor={colors?.primary?.rgba}
                  primary
                  onClick={() => {
                    redirect(customOption);
                  }}
                  className="call-us-btn"
                >
                  {customOption.buttonText}
                </CustomButton>
              )}
            </MainOuterCard>
          </div>
        )}
      </>
    );
  }

  function updateCustomPricingInputValue(value: string, index: number) {
    const clonedItems = items.map((item) => {
      return new CustomerFacingCheckOutItemModel(item);
    });

    clonedItems[index].customPricingInputValue = value;
    updateItems(clonedItems);
  }

  const itemsToShow = groupedItems.find(
    (item) =>
      item.name === (purchaseStore.activeNavPill || groupedItems[0].name)
  );

  const centerCustomOption =
    (customOption && purchaseStore.activeNavPill === "CUSTOM") ||
    (!groupByBillingFrequency && customOption)
      ? true
      : false;

  const isCentered =
    items?.length === 1 ||
    itemsToShow?.items?.length === 1 ||
    centerCustomOption;

  let isCenteredJustify = "flex-start";
  const itemsCount = groupedItems.length
    ? (itemsToShow?.items?.length || 0) + (customOption ? 1 : 0)
    : items.length + (customOption ? 1 : 0);
  switch (itemsCount) {
    case 5:
      isCenteredJustify = screenSize.width > 2500 ? "center" : "flex-start";
      break;
    case 4:
      isCenteredJustify = screenSize.width > 2000 ? "center" : "flex-start";
      break;
    case 3:
      isCenteredJustify = screenSize.width > 1500 ? "center" : "flex-start";
      break;
    case 2:
      isCenteredJustify = screenSize.width > 1000 ? "center" : "flex-start";
      break;
    default:
      isCenteredJustify = isCentered ? "center" : "flex-start";
      break;
  }

  const isRecommendedItem = recommendedItem && recommendedItem?.priceId !== "";
  const customSpacing =
    groupedItems.length > 1
      ? isRecommendedItem
        ? 55
        : 30
      : isRecommendedItem
        ? 40
        : 0;
  // const itemsPlacement = screenSize.width > 2000 ? " center" : "flex-start";

  const isRecommendedItemAndGroupedItems = isRecommendedItem
    ? ""
    : "divided-component";
  return (
    <FlexContainer justifyContent={isCenteredJustify}>
      <MultiplePriceWrapper
        className="multiple-item-with-tab full-height"
        bgColor={colors?.outerBackground?.rgba}
      >
        <FlexContainer
          spacing={30}
          className={`full-height ${isRecommendedItemAndGroupedItems}`}
          fxLayout="column"
          justifyContent="center"
          bgColor={colors?.innerBackground?.rgba}
        >
          {groupedItems.length > 0 && (
            <div className="full-width"> {navPills()}</div>
          )}
          <BundleFlexWrapper bundleSpacing={customSpacing}>
            <FlexContainer
              className={`bundle-flex`}
              // justifyContent={isCenteredJustify}
            >
              {renderItemsByActivePill()}
            </FlexContainer>
          </BundleFlexWrapper>
        </FlexContainer>
      </MultiplePriceWrapper>
    </FlexContainer>
  );
};

export default withRouter(observer(MultipleItems));
